import type { ReactNode } from 'react';

export interface NavItem {
  /** Route path */
  href: string;
  /** Display label (Russian) */
  label: string;
  /** Icon component */
  icon: ReactNode;
  /** If true, only visible to admin users */
  adminOnly?: boolean;
  /** If true, route must match exactly (no nested paths) */
  exact?: boolean;
  /** Additional route prefixes that should mark this item active */
  activePaths?: string[];
  /** When present, renders a sub-group label separator before this item */
  separator?: string;
}

export interface NavGroup {
  /** Group title */
  title: string;
  /** Items in this group */
  items: NavItem[];
}

export interface SecondaryNavItem {
  key: string;
  label: string;
  to: string;
  icon?: ReactNode;
  badge?: { count: number; tone: 'warning' | 'danger' };
  attention?: boolean;
  hidden?: boolean;
  exact?: boolean;
  adminOnly?: boolean;
}

export interface SecondaryNavGroup {
  label: string;
  items: SecondaryNavItem[];
}

const STOCK_CATALOG_ACTIVE_PATHS = [
  '/stock/ingredients',
  '/stock/supplies',
  '/stock/recipes',
  '/stock/balances',
] as const;

const STOCK_OPERATIONS_ACTIVE_PATHS = [
  '/stock/receipts',
  '/stock/transfers',
  '/stock/write-offs',
  '/stock/b2b-sales',
  '/stock/platform-sales',
  '/stock/sales-invoices',
  '/stock/supplier-returns',
  '/stock/productions',
  '/stock/hot-reports',
  '/stock/inventories',
] as const;

const SELLER_WORKSPACE_ACTIVE_PATHS = ['/stock/seller-workspace'] as const;

/**
 * Admins see it by role (`adminOnly` filters the admin navigation); operators get
 * `storeCamerasNavGroup` appended by the Sidebar only with a camera grant (MD-0086).
 */
const storeCamerasNavItem: NavItem = {
  href: '/cameras',
  label: 'Камеры магазинов',
  icon: <CameraIcon />,
  adminOnly: true,
};

export const storeCamerasNavGroup: NavGroup = { title: 'Камеры', items: [storeCamerasNavItem] };

export const topLevelItems: NavItem[] = [
  { href: '/invoices', label: 'Документы', icon: <InvoiceIcon /> },
  // One money section (MD-0100): К оплате, Разобрать, Движения, Терминалы, Счета, Сверка are its tabs.
  {
    href: '/payments',
    label: 'Деньги',
    icon: <PaymentsIcon />,
    activePaths: ['/bank', '/money', '/reconciliation/cases'],
  },
  { href: '/stores', label: 'Магазины', icon: <StoresIcon /> },
  storeCamerasNavItem,
  { href: '/references', label: 'Справочники', icon: <ReferencesIcon /> },
  { href: '/discounts', label: 'Скидки', icon: <DiscountsIcon /> },
];

export const bottomItems: NavItem[] = [
  {
    href: '/analytics',
    label: 'Отчёты',
    icon: <AnalyticsIcon />,
    activePaths: ['/gopos/orders', '/stock/operations', '/stock/movements'],
  },
  { href: '/settings', label: 'Настройки', icon: <SettingsIcon /> },
];

/**
 * Admins see it by role (`adminOnly` filters the admin navigation); operators get the whole group
 * appended by the Sidebar only with a payroll grant (MD-0097).
 */
export const productionPayrollNavGroup: NavGroup = {
  title: 'Производство',
  items: [
    {
      href: '/production/payroll',
      label: 'Зарплата производства',
      icon: <PayrollIcon />,
      adminOnly: true,
    },
  ],
};

export const navGroups: NavGroup[] = [
  {
    title: 'Склад',
    items: [
      {
        href: '/stock/products',
        label: 'Каталог',
        icon: <ProductsIcon />,
        activePaths: [...STOCK_CATALOG_ACTIVE_PATHS],
      },
      {
        href: '/stock/receipts',
        label: 'Операции',
        icon: <MovementsIcon />,
        activePaths: [...STOCK_OPERATIONS_ACTIVE_PATHS],
      },
      {
        href: '/stock/seller-workspace',
        label: 'Заявки продавцов',
        icon: <TransfersIcon />,
        activePaths: [...SELLER_WORKSPACE_ACTIVE_PATHS],
      },
    ],
  },
  // Admins see both payrolls in one group; a granted operator gets only the production group.
  {
    title: 'Зарплата',
    items: [
      ...productionPayrollNavGroup.items,
      {
        href: '/sellers/payroll',
        label: 'Зарплата продавцов',
        icon: <PayrollIcon />,
        adminOnly: true,
      },
    ],
  },
];

export const navItems: NavItem[] = [
  ...topLevelItems,
  ...navGroups.flatMap((group) => group.items),
  ...bottomItems,
];

export const analyticsSecondaryGroups: SecondaryNavGroup[] = [
  {
    label: 'Аналитика',
    items: [
      {
        key: 'dashboard',
        label: 'Дашборд',
        to: '/webadmin/app/analytics',
        icon: <AnalyticsIcon />,
        exact: true,
      },
      { key: 'sales', label: 'Продажи', to: '/webadmin/app/analytics/sales', icon: <SalesIcon /> },
      { key: 'stock', label: 'Склад', to: '/webadmin/app/analytics/stock', icon: <BalancesIcon /> },
      {
        key: 'expenses',
        label: 'Расходы',
        to: '/webadmin/app/analytics/expenses',
        icon: <InvoiceIcon />,
      },
      { key: 'pnl', label: 'P&L', to: '/webadmin/app/analytics/pnl', icon: <ReconciliationIcon /> },
      {
        key: 'raw-purchases',
        label: 'Сырьё',
        to: '/webadmin/app/analytics/raw-purchases',
        icon: <IngredientsIcon />,
      },
      {
        key: 'platforms',
        label: 'Платформы',
        to: '/webadmin/app/analytics/platforms',
        icon: <SalesIcon />,
      },
      {
        key: 'activity',
        label: 'Время команды',
        to: '/webadmin/app/analytics/activity',
        icon: <ClockIcon />,
        adminOnly: true,
      },
    ],
  },
  {
    label: 'Источники',
    items: [
      {
        key: 'stock-movements',
        label: 'Движения',
        to: '/webadmin/app/stock/operations',
        icon: <MovementsIcon />,
      },
      {
        key: 'gopos-orders',
        label: 'Журнал продаж',
        to: '/webadmin/app/gopos/orders',
        icon: <SalesIcon />,
      },
    ],
  },
];

export const stockCatalogSecondaryGroups: SecondaryNavGroup[] = [
  {
    label: 'Каталог',
    items: [
      {
        key: 'balances',
        label: 'Остатки',
        to: '/webadmin/app/stock/balances',
        icon: <BalancesIcon />,
      },
      {
        key: 'products',
        label: 'Товары',
        to: '/webadmin/app/stock/products',
        icon: <ProductsIcon />,
        exact: true,
      },
      {
        key: 'categories',
        label: 'Категории',
        to: '/webadmin/app/stock/products/categories',
        icon: <CategoriesIcon />,
      },
      {
        key: 'ingredients',
        label: 'Ингредиенты & ТМЦ',
        to: '/webadmin/app/stock/ingredients',
        icon: <IngredientsIcon />,
      },
      {
        key: 'recipes',
        label: 'Рецептуры',
        to: '/webadmin/app/stock/recipes',
        icon: <RecipesIcon />,
      },
    ],
  },
];

export const stockOperationsSecondaryGroups: SecondaryNavGroup[] = [
  {
    label: 'Операции',
    items: [
      {
        key: 'receipts',
        label: 'Приходы',
        to: '/webadmin/app/stock/receipts',
        icon: <ReceiptsIcon />,
      },
      {
        key: 'transfers',
        label: 'Перемещения',
        to: '/webadmin/app/stock/transfers',
        icon: <TransfersIcon />,
      },
      {
        key: 'write-offs',
        label: 'Списания',
        to: '/webadmin/app/stock/write-offs',
        icon: <WriteOffsIcon />,
      },
      {
        key: 'b2b-sales',
        label: 'B2B продажи',
        to: '/webadmin/app/stock/b2b-sales',
        icon: <SalesIcon />,
      },
      {
        key: 'platform-sales',
        label: 'Продажи через платформы',
        to: '/webadmin/app/stock/platform-sales',
        icon: <SalesIcon />,
      },
      {
        key: 'sales-invoices',
        label: 'Фактуры',
        to: '/webadmin/app/stock/sales-invoices',
        icon: <InvoiceIcon />,
        adminOnly: true,
      },
      {
        key: 'supplier-returns',
        label: 'Возвраты',
        to: '/webadmin/app/stock/supplier-returns',
        icon: <ReturnsIcon />,
      },
      {
        key: 'productions',
        label: 'Производство',
        to: '/webadmin/app/stock/productions',
        icon: <ProductionsIcon />,
      },
      {
        key: 'hot-reports',
        label: 'Отчёты горячего',
        to: '/webadmin/app/stock/hot-reports',
        icon: <ProductionsIcon />,
        adminOnly: true,
      },
      {
        key: 'inventories',
        label: 'Инвентаризации',
        to: '/webadmin/app/stock/inventories',
        icon: <InventoriesIcon />,
      },
    ],
  },
];

export const sellerWorkspacePrimaryGroups: NavGroup[] = [
  {
    title: 'Продавец',
    items: [
      {
        href: '/stock/seller-workspace',
        label: 'Заявки продавцов',
        icon: <TransfersIcon />,
        exact: true,
      },
      {
        href: '/stock/seller-workspace/receiving',
        label: 'Приёмка поставки',
        icon: <TransfersIcon />,
      },
      {
        href: '/stock/seller-workspace/balances',
        label: 'Остатки',
        icon: <BalancesIcon />,
      },
      {
        href: '/stock/seller-workspace/scanner',
        label: 'Сканер',
        icon: <ScannerIcon />,
      },
      {
        href: '/stock/seller-workspace/expiry',
        label: 'Сроки',
        icon: <ClockIcon />,
      },
      {
        href: '/stock/seller-workspace/counts',
        label: 'Пересчёт',
        icon: <InventoriesIcon />,
      },
      {
        href: '/stock/seller-workspace/sale-changes',
        label: 'Изменения продаж',
        icon: <TransfersIcon />,
      },
      {
        href: '/stock/seller-workspace/write-offs',
        label: 'Списания',
        icon: <WriteOffsIcon />,
      },
      {
        href: '/stock/seller-workspace/productions',
        label: 'Производство',
        icon: <ProductionsIcon />,
      },
    ],
  },
];

export const sellerWorkspaceSecondaryGroups: SecondaryNavGroup[] = [
  {
    label: 'Заявки продавцов',
    items: [
      {
        key: 'seller-workspace-requests',
        label: 'Заявки',
        to: '/webadmin/app/stock/seller-workspace',
        icon: <TransfersIcon />,
        exact: true,
      },
      {
        key: 'seller-workspace-receiving',
        label: 'Приёмка поставки',
        to: '/webadmin/app/stock/seller-workspace/receiving',
        icon: <TransfersIcon />,
      },
      {
        key: 'seller-workspace-balances',
        label: 'Остатки',
        to: '/webadmin/app/stock/seller-workspace/balances',
        icon: <BalancesIcon />,
      },
      {
        key: 'seller-workspace-scanner',
        label: 'Сканер',
        to: '/webadmin/app/stock/seller-workspace/scanner',
        icon: <ScannerIcon />,
      },
      {
        key: 'seller-workspace-expiry',
        label: 'Сроки',
        to: '/webadmin/app/stock/seller-workspace/expiry',
        icon: <ClockIcon />,
      },
      {
        key: 'seller-workspace-counts',
        label: 'Пересчёт',
        to: '/webadmin/app/stock/seller-workspace/counts',
        icon: <InventoriesIcon />,
      },
      {
        key: 'seller-workspace-sale-changes',
        label: 'Изменения продаж',
        to: '/webadmin/app/stock/seller-workspace/sale-changes',
        icon: <SaleChangesIcon />,
      },
      {
        key: 'seller-workspace-write-offs',
        label: 'Списания',
        to: '/webadmin/app/stock/seller-workspace/write-offs',
        icon: <WriteOffsIcon />,
      },
      {
        key: 'seller-workspace-productions',
        label: 'Производство',
        to: '/webadmin/app/stock/seller-workspace/productions',
        icon: <ProductionsIcon />,
      },
    ],
  },
];

// The owner's control pages first, then the same store sections a seller has (with
// «Сроки» as the seller sees it, owner 24.10.2026: «чтоб я мог тестить»).
export const sellerWorkspaceAdminSecondaryGroups: SecondaryNavGroup[] = [
  {
    label: 'Сеть',
    items: [
      {
        key: 'seller-workspace-owner-overview',
        label: 'Обзор сети',
        to: '/webadmin/app/stock/seller-workspace/overview',
        icon: <AnalyticsIcon />,
        exact: true,
      },
      {
        key: 'seller-workspace-inventory',
        label: 'Запасы и пополнение',
        to: '/webadmin/app/stock/seller-workspace/overview/inventory',
        icon: <BalancesIcon />,
        exact: true,
      },
      {
        key: 'seller-workspace-admin',
        label: 'Обзор заявок',
        to: '/webadmin/app/stock/seller-workspace/admin',
        icon: <ReconciliationIcon />,
        exact: true,
      },
      {
        key: 'seller-workspace-sale-reviews',
        label: 'Проверка изменений',
        to: '/webadmin/app/stock/seller-workspace/sale-reviews',
        icon: <ReconciliationIcon />,
      },
      {
        key: 'seller-workspace-expiry-risk',
        label: 'Сроки годности',
        to: '/webadmin/app/stock/seller-workspace/expiry-risk',
        icon: <ClockIcon />,
      },
      {
        key: 'seller-workspace-new-barcodes',
        label: 'Новые штрихкоды',
        to: '/webadmin/app/stock/seller-workspace/new-barcodes',
        icon: <ScannerIcon />,
      },
      {
        key: 'seller-workspace-access',
        label: 'Доступы',
        to: '/webadmin/app/stock/seller-workspace/access',
        icon: <SettingsIcon />,
      },
    ],
  },
  {
    label: 'Магазин',
    items: [
      {
        key: 'seller-workspace-requests',
        label: 'Заявки',
        to: '/webadmin/app/stock/seller-workspace/requests',
        icon: <TransfersIcon />,
        exact: true,
      },
      {
        key: 'seller-workspace-receiving',
        label: 'Приёмка поставки',
        to: '/webadmin/app/stock/seller-workspace/receiving',
        icon: <ReceiptsIcon />,
      },
      {
        key: 'seller-workspace-balances',
        label: 'Остатки',
        to: '/webadmin/app/stock/seller-workspace/balances',
        icon: <BalancesIcon />,
      },
      {
        key: 'seller-workspace-scanner',
        label: 'Сканер',
        to: '/webadmin/app/stock/seller-workspace/scanner',
        icon: <ScannerIcon />,
      },
      {
        key: 'seller-workspace-expiry',
        label: 'Сроки',
        to: '/webadmin/app/stock/seller-workspace/expiry',
        icon: <ClockIcon />,
      },
      {
        key: 'seller-workspace-counts',
        label: 'Пересчёт',
        to: '/webadmin/app/stock/seller-workspace/counts',
        icon: <InventoriesIcon />,
      },
      {
        key: 'seller-workspace-sale-changes',
        label: 'Изменения продаж',
        to: '/webadmin/app/stock/seller-workspace/sale-changes',
        icon: <SaleChangesIcon />,
      },
      {
        key: 'seller-workspace-write-offs',
        label: 'Списания',
        to: '/webadmin/app/stock/seller-workspace/write-offs',
        icon: <WriteOffsIcon />,
      },
      {
        key: 'seller-workspace-productions',
        label: 'Производство',
        to: '/webadmin/app/stock/seller-workspace/productions',
        icon: <ProductionsIcon />,
      },
    ],
  },
];

const navIconProps = {
  className: 'size-5',
  fill: 'none',
  viewBox: '0 0 24 24',
  stroke: 'currentColor',
  strokeWidth: 1.5,
};

function ScannerIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 7.5V5.25A1.5 1.5 0 0 1 5.25 3.75H7.5M16.5 3.75h2.25a1.5 1.5 0 0 1 1.5 1.5V7.5M20.25 16.5v2.25a1.5 1.5 0 0 1-1.5 1.5H16.5M7.5 20.25H5.25a1.5 1.5 0 0 1-1.5-1.5V16.5M7.5 8.25v7.5M10.5 8.25v7.5M13.5 8.25v7.5M16.5 8.25v7.5"
      />
    </svg>
  );
}

function PayrollIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
      />
    </svg>
  );
}

function InvoiceIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
      />
    </svg>
  );
}

function PaymentsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"
      />
    </svg>
  );
}

function AnalyticsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
}

function ReconciliationIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
      />
    </svg>
  );
}

function ReferencesIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
      />
    </svg>
  );
}

function SalesIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
      />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function ProductsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
      />
    </svg>
  );
}

function IngredientsIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.75}
      stroke="currentColor"
      className="size-5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.25 4.5v5.25c0 .597.237 1.17.659 1.591l4.341 4.341A2.25 2.25 0 0 1 17.659 19.5H6.34a2.25 2.25 0 0 1-1.591-3.841l4.341-4.341A2.25 2.25 0 0 0 9.75 9.75V4.5m4.5 0h-4.5m4.5 0h1.5m-6 0h-1.5"
      />
    </svg>
  );
}

function RecipesIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
      />
    </svg>
  );
}

function BalancesIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
      />
    </svg>
  );
}

function MovementsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
      />
    </svg>
  );
}

function ReceiptsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 3.75H6.912a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H15M2.25 13.5h6.364a.75.75 0 0 1 .66.388l.734 1.467a.75.75 0 0 0 .66.389h2.664a.75.75 0 0 0 .66-.39l.734-1.466a.75.75 0 0 1 .66-.388h6.364"
      />
    </svg>
  );
}

function TransfersIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 7.5h-.75A2.25 2.25 0 0 0 4.5 9.75v7.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25v-7.5a2.25 2.25 0 0 0-2.25-2.25h-.75m0-3-3-3m0 0-3 3m3-3v11.25m6-2.25h.75a2.25 2.25 0 0 1 2.25 2.25v7.5a2.25 2.25 0 0 1-2.25 2.25h-7.5a2.25 2.25 0 0 1-2.25-2.25v-.75"
      />
    </svg>
  );
}

function WriteOffsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
      />
    </svg>
  );
}

function ProductionsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
      />
    </svg>
  );
}

function InventoriesIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
      />
    </svg>
  );
}

function ReturnsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 6.75 3.75 12m0 0L9 17.25M3.75 12H15a4.5 4.5 0 0 1 4.5 4.5v1.5"
      />
    </svg>
  );
}

function CategoriesIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
    </svg>
  );
}

function StoresIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z"
      />
    </svg>
  );
}

function DiscountsIcon() {
  return (
    <svg {...navIconProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.75 9a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.5 4.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
      />
    </svg>
  );
}

function SaleChangesIcon() {
  return (
    <svg
      className="size-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M16 3l5 5M9 15l-1 5 5-1 9-9a2.12 2.12 0 0 0-5-5l-8 10Z"
      />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <rect x="3" y="5" width="13" height="14" rx="2" />
      <path d="m16 10 5-3v10l-5-3" />
    </svg>
  );
}
